(() => {
  if (customElements.get('bf-fan')) return;
  const mod = (value, size) => (value % size + size) % size;
  class BFFan extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      const { signal } = this.lifecycle;
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.stage = this.querySelector('[data-stage]');
      this.center = this.querySelector('[data-center]');
      this.collectionTitle = this.center?.querySelector('[data-collection-title]');
      this.window = this.querySelector('[data-window]');
      this.shop = this.querySelector('[data-shop]');
      this.caption = this.querySelector('[data-caption]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = Math.min(Math.max(Number(this.dataset.initial) || 0, 0), this.cards.length - 1);
      this.targetIndex = this.index;
      this.transitionVersion = (this.transitionVersion || 0) + 1;
      this.transitioning = false;
      this.wheelUsed = false;
      this.wheelTotal = 0;
      this.paused = this.dataset.autoplay !== 'true' || this.dataset.editor === 'true';
      this.hovered = false;
      this.focused = false;
      this.visible = !('IntersectionObserver' in window);
      if (!this.cards.length) return;
      this.classList.add('is-enhanced');
      this.querySelectorAll('[data-prev], [data-next]').forEach((button) => button.disabled = this.cards.length < 2);
      this.addEventListener('click', (event) => {
        if (this.suppressClick && event.target.closest('[data-select], [data-shop]')) { event.preventDefault(); return; }
        const select = event.target.closest('[data-select]');
        if (select) {
          event.preventDefault();
          this.select(this.cards.indexOf(select.closest('[data-card]')), true);
          if (event.detail === 0) (this.shop.hidden ? this.stage : this.shop).focus({ preventScroll: true });
        }
        if (event.target.closest('[data-prev]')) this.select(this.targetIndex - 1, true);
        if (event.target.closest('[data-next]')) this.select(this.targetIndex + 1, true);
      }, { signal });
      this.stage.addEventListener('wheel', (event) => this.onWheel(event), { passive: false, signal });
      this.stage.addEventListener('keydown', (event) => {
        const next = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.cards.length - 1 }[event.key];
        if (next === undefined) return;
        event.preventDefault(); this.select(next, true); this.stage.focus({ preventScroll: true });
      }, { signal });
      this.stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' || !event.isPrimary) return;
        this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }, { signal });
      this.stage.addEventListener('pointerup', (event) => {
        if (event.pointerId !== this.pointer?.id) return;
        const dx = event.clientX - this.pointer.x, dy = event.clientY - this.pointer.y;
        this.pointer = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy) * 1.3) return;
        this.suppressClick = true;
        clearTimeout(this.clickTimer);
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 400);
        this.select(this.index + (dx < 0 ? 1 : -1), true);
      }, { signal });
      this.stage.addEventListener('pointercancel', () => { this.pointer = null; }, { signal });
      this.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); } }, { signal });
      this.addEventListener('pointerleave', () => { this.hovered = false; this.pointer = null; this.schedule(); }, { signal });
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, { signal });
      this.addEventListener('focusout', (event) => { this.focused = this.contains(event.relatedTarget); this.schedule(); }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.motion.addEventListener('change', () => this.select(this.targetIndex, false, false), { signal });
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.cards.findIndex((card) => card.dataset.blockId === event.detail?.blockId);
        if (index === -1) return;
        this.paused = true; this.select(index, true, false);
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.renderSides());
      this.resizeObserver.observe(this.stage);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.schedule(); }, { threshold: .15 });
        this.observer.observe(this);
      }
      this.select(this.index, false, false);
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.resizeObserver?.disconnect();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.clickTimer);
      clearTimeout(this.wheelTimer);
      this.transitionVersion++;
      this.stopAnimation();
      this.pointer = null;
      this.suppressClick = false;
    }

    renderSides() {
      const width = this.stage.clientWidth;
      const first = Math.min(190, width * .29), step = Math.min(120, width * .19);
      this.cards.forEach((card, index) => {
        let diff = mod(index - this.index, this.cards.length);
        if (diff > this.cards.length / 2) diff -= this.cards.length;
        const x = diff ? Math.sign(diff) * (first + (Math.abs(diff) - 1) * step) : 0;
        const wrapped = card.dataset.diff !== undefined && Math.abs(Number(card.dataset.diff) - diff) > this.cards.length / 2;
        if (wrapped) card.style.transition = 'none';
        card.style.transform = `translate(-50%, -50%) translateX(${x}px) rotateY(${diff > 0 ? -45 : diff < 0 ? 45 : 0}deg)`;
        card.style.zIndex = String(20 - Math.abs(diff));
        card.dataset.diff = String(diff);
        card.classList.toggle('is-current', diff === 0);
        card.setAttribute('aria-hidden', String(diff === 0));
        card.querySelector('[data-select]').tabIndex = diff !== 0 && Math.abs(x) < width / 2 + 20 ? 0 : -1;
        if (wrapped) { void card.offsetWidth; card.style.transition = ''; }
      });
    }

    select(index, manual = false, animate = true) {
      if (!this.cards.length) return;
      this.targetIndex = mod(index, this.cards.length);
      this.manualSelection = manual;
      clearTimeout(this.timer);
      if (!animate || this.motion.matches || !this.window.firstElementChild?.animate) {
        this.transitionVersion++;
        this.transitioning = false;
        this.renderSelection(this.targetIndex, manual, false);
        return;
      }
      if (!this.transitioning) this.advance();
    }

    advance() {
      if (this.index === this.targetIndex) { this.schedule(); return; }
      let distance = mod(this.targetIndex - this.index, this.cards.length);
      if (distance > this.cards.length / 2) distance -= this.cards.length;
      this.transitioning = true;
      const version = this.transitionVersion;
      this.renderSelection(this.index + Math.sign(distance), this.manualSelection).then(() => {
        if (!this.isConnected || version !== this.transitionVersion) return;
        this.transitioning = false;
        this.advance();
      });
    }

    onWheel(event) {
      if (event.ctrlKey || event.metaKey || this.cards.length < 2 ||
        event.target.closest('input,textarea,select,[contenteditable="true"]')) return;
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      const direction = Math.sign(delta);
      if (!direction) return;
      // Like the hero, wheel gestures release the page at either end; arrows still loop.
      if (!this.transitioning && ((direction < 0 && this.index === 0) || (direction > 0 && this.index === this.cards.length - 1))) return;
      event.preventDefault();
      clearTimeout(this.wheelTimer);
      this.wheelTimer = setTimeout(() => { this.wheelUsed = false; this.wheelTotal = 0; this.wheelDirection = 0; }, 240);
      if (this.wheelUsed || this.transitioning) return;
      if (direction !== this.wheelDirection) this.wheelTotal = 0;
      this.wheelDirection = direction;
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.stage.clientHeight : 1;
      this.wheelTotal += delta * unit;
      if (Math.abs(this.wheelTotal) < 28) return;
      this.wheelUsed = true;
      this.wheelTotal = 0;
      this.select(this.index + direction, true);
    }

    renderSelection(index, manual = false, animate = true) {
      const previous = this.index;
      this.index = mod(index, this.cards.length);
      this.stopAnimation();
      const outgoing = this.window.firstElementChild;
      const incoming = this.cards[this.index].querySelector('img, svg').cloneNode(true);
      incoming.setAttribute('loading', 'eager');
      this.window.replaceChildren(incoming);
      const card = this.cards[this.index];
      const collectionTitle = card.dataset.collectionTitle || card.dataset.title;
      if (this.collectionTitle) this.collectionTitle.textContent = collectionTitle;
      this.shop.hidden = !card.dataset.url;
      if (card.dataset.url) this.shop.setAttribute('href', card.dataset.url);
      else this.shop.removeAttribute('href');
      this.shop.setAttribute('aria-label', `${this.dataset.shopLabel} — ${collectionTitle}`);
      this.caption.setAttribute('aria-live', manual && this.index === this.targetIndex ? 'polite' : 'off');
      this.caption.textContent = `${card.dataset.title} · ${this.index + 1} / ${this.cards.length}`;
      this.renderSides();
      let finished = Promise.resolve();
      if (animate && previous !== this.index && !this.motion.matches && incoming.animate) {
        const side = index > previous ? 1 : -1;
        outgoing.setAttribute('aria-hidden', 'true');
        outgoing.dataset.transition = '';
        this.window.append(outgoing);
        const timing = { duration: 520, easing: 'cubic-bezier(.42,0,.58,1)' };
        this.animations = [
          incoming.animate([{ transform: `translateX(${side * 105}%) rotateY(${-side * 72}deg) scale(.88)`, opacity: .3 }, { transform: 'translateX(0) rotateY(0) scale(1.08)', opacity: 1, offset: .76 }, { transform: 'translateX(0) rotateY(0) scale(1)' }], timing),
          outgoing.animate([{ transform: 'translateX(0) rotateY(0)' }, { transform: `translateX(${-side * 105}%) rotateY(${side * 72}deg) scale(.9)`, opacity: 0 }], { ...timing, fill: 'forwards' }),
          this.center.animate([{ transform: 'translate(-50%, -50%) scale(1)' }, { transform: 'translate(-50%, -50%) scale(1.07)', offset: .62 }, { transform: 'translate(-50%, -50%) scale(1)' }], timing)
        ];
        finished = Promise.allSettled(this.animations.map((animation) => animation.finished)).then(() => outgoing.remove());
      }
      this.schedule();
      return finished;
    }

    stopAnimation() {
      this.animations?.forEach((animation) => animation.cancel());
      this.animations = [];
      this.window?.querySelectorAll('[data-transition]').forEach((node) => node.remove());
    }

    schedule() {
      clearTimeout(this.timer);
      const stopped = this.paused || this.motion.matches;
      if (stopped || this.transitioning || this.hovered || this.focused || !this.visible || document.hidden || this.cards.length < 2) return;
      this.timer = setTimeout(() => this.select(this.index + 1), Math.max(3, Number(this.dataset.interval) || 4) * 1000);
    }
  }
  customElements.define('bf-fan', BFFan);
})();
